import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Clock, Scissors } from "lucide-react";
import { toast } from "sonner";

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", duration: 30, price: 25 });

  useEffect(() => {
    load();
  }, []);

  const load = () => API.get("/services").then(r => setServices(r.data)).catch(() => {});

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", duration: 30, price: 25 });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, duration: s.duration, price: s.price });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Entrez un nom"); return; }
    try {
      if (editing) {
        await API.put(`/services/${editing.id}`, form);
        toast.success("Service mis a jour");
      } else {
        await API.post("/services", { ...form, active: true });
        toast.success("Service cree");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/services/${id}`);
      toast.success("Service supprime");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const toggleActive = async (s) => {
    try {
      await API.put(`/services/${s.id}`, { active: !s.active });
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="services-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Services</h1>
        <Button onClick={openCreate} className="rounded-full gap-2 bg-primary hover:bg-blue-700 text-sm" data-testid="add-service-btn">
          <Plus size={16} /> Ajouter
        </Button>
      </div>

      {services.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-10 text-center">
          <Scissors size={32} className="text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun service. Ajoutez vos premiers services.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {services.map((s) => (
            <div
              key={s.id}
              className={`bg-white rounded-xl border border-border p-5 flex items-center justify-between transition-opacity ${!s.active ? "opacity-50" : ""}`}
              data-testid={`service-${s.id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.active ? "bg-primary/10" : "bg-muted"}`}>
                  <Scissors size={18} className={s.active ? "text-primary" : "text-muted-foreground"} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{s.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock size={10} /> {s.duration} min
                    </span>
                    <span className="font-mono text-xs font-semibold">{s.price} EUR</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={s.active} onCheckedChange={() => toggleActive(s)} data-testid={`toggle-${s.id}`} />
                <Button variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-8 w-8" data-testid={`edit-${s.id}`}>
                  <Pencil size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-${s.id}`}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier le service" : "Nouveau service"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm mb-1.5 block">Nom</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Coupe homme" className="h-11" data-testid="service-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm mb-1.5 block">Duree (min)</Label>
                <Input type="number" value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) || 0 })} className="h-11" data-testid="service-duration-input" />
              </div>
              <div>
                <Label className="text-sm mb-1.5 block">Prix (EUR)</Label>
                <Input type="number" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="h-11" data-testid="service-price-input" />
              </div>
            </div>
            <Button onClick={handleSave} className="w-full rounded-full h-11 bg-primary hover:bg-blue-700" data-testid="save-service-btn">
              {editing ? "Mettre a jour" : "Creer le service"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
